import { useEffect, useRef, useState } from 'react';

interface StellenanzeigeLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Speichert; Leerstring entfernt den Link. */
  onSave: (url: string) => Promise<void>;
  isPending: boolean;
  kanalLabel: string;
  /** Kind bzw. Ort — damit im Popup steht, worum es geht. */
  titel: string;
  currentUrl: string | null;
}

/**
 * Ergänzt oder ändert den Link einer Anzeige — im Aufbau wie `StadtModal`, damit
 * sich das Anlegen einer Kleinigkeit überall in der App gleich anfühlt.
 */
export function StellenanzeigeLinkModal({
  isOpen,
  onClose,
  onSave,
  isPending,
  kanalLabel,
  titel,
  currentUrl,
}: StellenanzeigeLinkModalProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isEditMode = !!currentUrl;

  const warOffen = useRef(false);

  /**
   * Das Feld wird nur beim Öffnen befüllt, nicht bei jeder Änderung von
   * `currentUrl`. Seit die Seite pollt, kann sich der Wert unter einer offenen
   * Eingabe ändern — jemand anderes bearbeitet dieselbe Anzeige —, und das darf
   * das Getippte nicht wegwerfen.
   */
  useEffect(() => {
    if (isOpen && !warOffen.current) {
      setUrl(currentUrl ?? '');
      setError(null);
      setTimeout(() => inputRef.current?.select(), 100);
    }
    warOffen.current = isOpen;
  }, [isOpen, currentUrl]);

  const handleSubmit = async () => {
    const roh = url.trim();

    // Leer heißt: Link entfernen. Das ist ein gültiger Wunsch, keine Fehleingabe.
    if (!roh) {
      setError(null);
      await onSave('');
      onClose();
      return;
    }

    // Wer eine Adresse aus der Adresszeile kopiert, hat das Schema dabei; wer sie
    // abtippt, meist nicht. Ergänzen ist freundlicher als eine Fehlermeldung.
    const mitSchema = /^https?:\/\//i.test(roh) ? roh : `https://${roh}`;
    try {
      new URL(mitSchema);
    } catch {
      setError('Das sieht nicht nach einer Adresse aus');
      return;
    }

    setError(null);
    try {
      await onSave(mitSchema);
      onClose();
    } catch {
      setError('Link konnte nicht gespeichert werden');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-md sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"
                  />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1 min-w-0">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {isEditMode ? 'Link bearbeiten' : 'Link hinterlegen'}
                </h3>
                <p className="mt-1 text-sm text-gray-500 truncate">
                  {kanalLabel} · {titel}
                </p>

                {error && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div className="mt-4">
                  <label
                    htmlFor="anzeigeUrl"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Adresse der Anzeige
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    id="anzeigeUrl"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="https://…"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Unter dieser Adresse steht die Anzeige — sie wird zum Abschalten
                    wieder gebraucht. Leer lassen entfernt den Link.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {isPending ? 'Wird gespeichert...' : 'Speichern'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
